import * as React from 'react'
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  PressableStateCallbackType,
  ViewStyle,
  StyleProp,
} from 'react-native'
import Link from 'next/link'
import { FontAwesomeIcon, IconName, findIconDefinition } from '@purposity/icons'
import { Spacer } from '@components/Spacer'
import * as Colors from '@themes/Colors'
import Fonts from '@themes/Fonts'

type ButtonProps = {
  title: string
  icon: IconName | null
  isActive: boolean
  href: string
  engaged: boolean
  styles?: StyleProp<ViewStyle>
  onPress?: () => void
  size?: 'normal' | 'small'
}

export function NavButton(props: ButtonProps) {
  return (
    <Pressable
      style={[styles.buttonContainer, props.styles]}
      onPress={props.onPress}
    >
      {({ hovered, pressed }) =>
        props.onPress ? (
          <NavButtonView hovered={hovered} pressed={pressed} {...props} />
        ) : (
          <Link href={props.href} passHref>
            <NavButtonView hovered={hovered} pressed={pressed} {...props} />
          </Link>
        )
      }
    </Pressable>
  )
}

function NavButtonView(props: ButtonProps & PressableStateCallbackType) {
  const isEngaged =
    props.hovered || props.pressed || props.engaged || props.isActive
  const isSmall = props.size === 'small'

  return (
    <View
      focusable
      accessible
      accessibilityRole="link"
      style={[
        styles.button,
        isEngaged && styles.hovered,
        isSmall && styles.buttonSmall,
      ]}
    >
      <Spacer width={12} height={1} noShrink style={{ alignSelf: 'center' }} />
      {props.icon && (
        <>
          <FontAwesomeIcon
            icon={findIconDefinition({
              prefix: isEngaged ? 'fas' : 'fal',
              iconName: props.icon,
            })}
            color={Colors.black}
            size={isSmall ? 16 : 19}
          />
          <Spacer
            width={19}
            height={1}
            noShrink
            style={{ alignSelf: 'center' }}
          />
        </>
      )}
      <Text
        style={[
          styles.buttonText,
          props.isActive && styles.buttonTextActive,
          isSmall && styles.buttonTextSmall,
        ]}
      >
        {props.title}
      </Text>
      <Spacer width={16} height={1} noShrink style={{ alignSelf: 'center' }} />
    </View>
  )
}

const styles = StyleSheet.create({
  buttonContainer: {
    width: '100%',
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
  },
  button: {
    width: 'fit-content',
    height: 46,
    minWidth: 46,
    flexShrink: 0,
    flexGrow: 0,
    flexDirection: 'row',
    fontSize: 20,
    justifyContent: 'center',
    alignItems: 'center',
    color: Colors.alternateGray,
    backgroundColor: Colors.white,
    borderRadius: 23,
    userSelect: 'none',
  },

  hovered: {
    backgroundColor: Colors.altWhite,
  },
  buttonText: {
    fontSize: 20,
    lineHeight: 19,
    fontFamily: Fonts.type.bold,
    fontWeight: '500',
    color: Colors.alternateGray,
    marginBottom: 4,
  },
  buttonTextActive: {
    fontWeight: '700',
  },
  buttonSmall: {
    height: 40,
    minWidth: 40,
  },
  buttonTextSmall: {
    fontSize: 16,
    lineHeight: 16,
  },
})
